import React, { useState } from 'react';
import {
    Button,
    Popover,
    List,
    ListItem,
    ListItemText,
    Typography,
    Divider,
    Box,
    makeStyles,
    Paper,
    Tooltip,
    IconButton,
    Dialog,
    DialogContent,
    DialogTitle,
} from '@material-ui/core';
import { InfoOutlined, TrendingUp, Warning, Close as CloseIcon } from '@material-ui/icons';
import LikeDislikeComponent from 'components/Reaction/LikeDislikeComponent';

const useStyles = makeStyles((theme) => ({
    root: {
        position: 'relative',
        width: theme.spacing(6),
        [theme.breakpoints.down('sm')]: {
            width: '100%'
        }
    },
    scoreButton: {
        minWidth: theme.spacing(6),
        height: theme.spacing(3.5),
        borderRadius: theme.spacing(1.75),
        padding: theme.spacing(0.5),
        fontSize: '0.875rem',
        fontWeight: 500,
        [theme.breakpoints.down('sm')]: {
            width: '100%',
            height: 'auto',
            padding: theme.spacing(1)
        }
    },
    popoverContent: {
        padding: theme.spacing(2),
        maxHeight: '80vh',
        overflow: 'auto',
        width: '320px',
        [theme.breakpoints.down('sm')]: {
            width: '100%',
            maxWidth: '100vw',
            height: '100vh',
            maxHeight: '100vh'
        }
    },
    categoryBox: {
        marginBottom: theme.spacing(1.5),
        borderRadius: 4,
        backgroundColor: theme.palette.background.default
    },
    scoreItem: {
        padding: theme.spacing(1),
        borderLeft: '2px solid',
        margin: theme.spacing(0.5, 0),
        '&:hover': {
            backgroundColor: theme.palette.action.hover
        },
        [theme.breakpoints.down('sm')]: {
            borderLeft: 'none',
            borderTop: '2px solid',
            padding: theme.spacing(1.5)
        }
    },
    scoreHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: theme.spacing(0.5),
        [theme.breakpoints.down('sm')]: {
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: theme.spacing(1)
        }
    },
    scoreLabel: {
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
        textTransform: 'uppercase',
        letterSpacing: '0.5px'
    },
    scoreValue: {
        fontSize: '0.875rem',
        fontWeight: 500
    },
    factList: {
        margin: 0,
        padding: theme.spacing(0, 0, 0, 2),
        listStyle: 'none',
        borderLeft: `1px dashed ${theme.palette.divider}`,
        [theme.breakpoints.down('sm')]: {
            padding: theme.spacing(1, 0),
            marginTop: theme.spacing(1),
            borderLeft: 'none',
            borderTop: `1px dashed ${theme.palette.divider}`
        }
    },
    factItem: {
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
        padding: theme.spacing(0.25, 0)
    },
    finalScoreSection: {
        marginTop: theme.spacing(2),
        padding: theme.spacing(1.5),
        borderRadius: 4,
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        [theme.breakpoints.down('sm')]: {
            position: 'sticky',
            bottom: 0,
            left: 0,
            right: 0,
            borderRadius: 0,
            marginTop: 'auto',
            textAlign: 'center'
        }
    },
    popupHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: `1px solid ${theme.palette.divider}`,
        padding: theme.spacing(1, 2),
        position: 'sticky',
        top: 0,
        backgroundColor: theme.palette.background.paper,
        zIndex: 1,
        [theme.breakpoints.down('sm')]: {
            padding: theme.spacing(1.5, 2)
        }
    },
    closeButton: {
        padding: theme.spacing(0.5),
        '&:hover': {
            backgroundColor: theme.palette.action.hover
        }
    },
    contentWrapper: {
        padding: theme.spacing(2),
        [theme.breakpoints.down('sm')]: {
            paddingBottom: theme.spacing(8) // Space for sticky footer
        }
    },
    dialogPaper: {
        width: '100%',
        maxWidth: '400px',
        margin: theme.spacing(2),
        [theme.breakpoints.down('sm')]: {
            margin: 0,
            maxWidth: '100%',
            height: '100%',
            maxHeight: '100%',
            borderRadius: 0
        }
    },
    dialogContent: {
        padding: 0,
        '&:first-child': {
            paddingTop: 0
        }
    }
}));

/**
 * SAMPLE scroreData
 * {
  "defaultScore": 100,
  "dispositionScore": 15,
  "entryBaseScore": 0,
  "talkTimeScore": 0,
  "customScore": 20,
  "remarketingScore": 5,
  "finalScore": 40,
  "scoringReasons": {
    "disposition": [
      {
        "rule": "disposition",
        "score": 15,
        "reason": "Rule disposition triggered for category disposition",
        "facts": {
          "dispositionArray": {
            "fact": "dispositionArray",
            "path": "$.priority",
            "operator": "notExists",
            "value": 0,
            "factResult": null,
            "valueResult": 0,
            "result": true
          }
        }
      }
    ],
    "entryBase": [
      {
        "rule": "prospect inserted",
        "score": 0,
        "reason": "Rule prospect inserted triggered for category entryBase",
        "facts": {
          "updatedCount": {
            "fact": "updatedCount",
            "operator": "equal",
            "value": 0,
            "factResult": 0,
            "valueResult": 0,
            "result": true
          }
        }
      }
    ],
    "talktime": [
      
    ],
    "custom": [
      {
        "rule": "source any integration",
        "score": 20,
        "reason": "Rule source any integration triggered for category custom",
        "facts": {
          "source": {
            "fact": "source",
            "operator": "notEqual",
            "value": "Bulk Upload",
            "factResult": null,
            "valueResult": "Bulk Upload",
            "result": true
          }
        }
      }
    ],
    "remarketingImpact": [
      {
        "rule": "communication",
        "source": "whatsapp",
        "value": 1,
        "score": 5,
        "reason": "Remarketing impact from whatsapp > 0",
        "facts": {
          "sendWelcomeMessage": {
            "fact": "sendWelcomeMessage",
            "operator": "equal",
            "value": true,
            "factResult": true,
            "valueResult": true,
            "result": true
          }
        }
      }
    ],
    "resetOnDisposition": [
      
    ]
  }
}
 */

const LeadScorePopupWithList = ({ scoreData = {} }) => {
    const classes = useStyles();
    const [open, setOpen] = useState(false);

    const handleClick = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const getScoreColor = (score) => {
        if (score >= 80) return '#4caf50';
        if (score >= 60) return '#2196f3';
        if (score >= 40) return '#ff9800';
        return '#f44336';
    };

    const getScoreIcon = (score) => {
        if (score >= 60) return <TrendingUp color="primary" />;
        return <Warning color="error" />;
    };

    const renderScoreCategory = (category, rules = []) => {
        if (!rules || rules.length === 0) return null;

        const formatFactKey = (key) => {
            return key
                .replace(/([A-Z])/g, ' $1')
                .split('_')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ')
                .trim();
        };

        const getReadableFact = (fact) => {
            try {
                const factData = typeof fact === 'string' ? JSON.parse(fact) : fact;

                const operatorMap = {
                    'equal': 'matches',
                    'notEqual': 'differs from',
                    'notExists': 'is absent',
                    'exists': 'is present'
                };

                const getDisplayValue = () => {
                    if (factData.operator === 'exists' || factData.operator === 'notExists') {
                        return '';
                    }
                    const value = factData.valueResult !== undefined ? factData.valueResult : factData.value;
                    return value === true ? ' ✓' : value === false ? ' ✗' : ` "${value}"`;
                };

                const getStatus = () => {
                    const emoji = factData.result ? '✅' : '❌';
                    const status = factData.result ? 'Condition Met' : 'Condition Not Met';
                    return `${emoji} ${status}`;
                };

                const factName = formatFactKey(factData.fact);
                const operator = operatorMap[factData.operator] || factData.operator;
                const displayValue = getDisplayValue();
                const status = getStatus();

                return `${factName} ${operator}${displayValue} | ${status}`;
            } catch (error) {
                console.error('Error parsing fact:', error);
                return '⚠️ Unable to analyze fact';
            }
        };

        return (
            <Box key={category}>
                {rules.map((rule, index) => (
                    <Paper
                        key={index}
                        className={classes.scoreItem}
                        style={{ borderLeftColor: getScoreColor(rule.score || 0) }}
                        elevation={0}
                    >
                        <Typography className={classes.scoreHeader}>
                            {category.charAt(0).toUpperCase() + category.slice(1)}
                        </Typography>
                        <Typography className={classes.scoreValue}>
                            {rule.score > 0 ? `+${rule.score}` : rule.score} • {rule.reason}
                        </Typography>
                        {rule.facts && Object.entries(rule.facts).map(([key, value], i) => (
                            <Typography key={i} className={classes.factItem}>
                                {getReadableFact(value)}
                            </Typography>
                        ))}
                    </Paper>
                ))}
            </Box>
        );
    };

    return (
        <div className={classes.root}>
            <Tooltip title="View Score Analysis" arrow>
                <Button
                    onClick={handleClick}
                    className={classes.scoreButton}
                    style={{
                        backgroundColor: `${getScoreColor(scoreData.finalScore || 0)}15`,
                        color: getScoreColor(scoreData.finalScore || 0)
                    }}
                >
                    {scoreData.finalScore || 0}
                </Button>
            </Tooltip>

            <Dialog
                open={open}
                onClose={handleClose}
                classes={{
                    paper: classes.dialogPaper
                }}
                fullScreen={window.innerWidth <= 600}
            >
                <DialogContent className={classes.dialogContent}>
                    <div className={classes.popupHeader}>
                        <Typography variant="h6">
                            Lead Score Analysis
                        </Typography>
                        <IconButton
                            className={classes.closeButton}
                            onClick={handleClose}
                            size="small"
                            aria-label="close"
                        >
                            <CloseIcon />
                        </IconButton>
                    </div>

                    <div className={classes.contentWrapper}>
                        <Box mb={2}>
                            <Typography variant="subtitle2" color="textSecondary">
                                 Score
                            </Typography>
                            <Typography variant="h6">
                            {scoreData.finalScore || 0} / {scoreData.defaultScore || 0}
                            </Typography>
                        </Box>

                        {Object.entries(scoreData.scoringReasons || {}).map(([category, rules]) =>
                            renderScoreCategory(category, rules)
                        )}

                           
                            <Box mt={2}>
                                <LikeDislikeComponent
                                    itemId="LeadScore" // Assuming your item has an _id
                                    itemType="LeadScoreReport" // Define a unique itemType
                                />
                            </Box>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default LeadScorePopupWithList;